import { useMemoizedFn } from 'ahooks';
import { find } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Form,
  Input,
  Link,
  MessagePlugin,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
} from 'tdesign-react';
import ListPanel from '@/components/ListPanel';
import useTable from '@/hooks/useTable';
import { deleteUser, getUserList } from '@/services/userService';
import { fetchRoleList } from '@/slices/roleSlice';
import { renderDateTime, renderTags, toTsOptions } from '@/utils/common';
import { userStatusOptions } from '@/utils/constant';
import PermWrapper from '@/components/PermWrapper';
import FormModal from './FormModal';
import StatusModal from './StatusModal';

const { FormItem } = Form;

function User({ fetchRoleList, roles }) {
  const [visible, setVisible] = useState(false);
  const [statusVisible, setStatusVisible] = useState(false);
  const [detail, setDetail] = useState(null);

  const options = useMemo(
    () => toTsOptions(roles.map((item) => ({ value: item.id, label: item.name }))),
    [roles],
  );

  const columns = [
    {
      title: '用户名称',
      colKey: 'username',
      cell: renderTags,
    },
    {
      title: '用户账号',
      colKey: 'account',
    },

    {
      title: '角色',
      colKey: 'roleIds',
      cell: ({ row }) => (
        <Space size='small' breakLine>
          {row.roleIds?.map((roleId) => {
            const name = find(roles, { id: roleId })?.name;
            return (
              <Tag theme={name === '超级管理员' ? 'danger' : 'default'} key={roleId}>
                {name}
              </Tag>
            );
          })}
        </Space>
      ),
    },
    {
      title: '状态',
      colKey: 'status',
      cell: ({ row }) => find(userStatusOptions, { value: row.status })?.render,
      width: 100,
    },
    {
      title: '创建时间',
      colKey: 'addTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      title: '更新时间',
      colKey: 'updateTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      cell: ({ row }) => (
        <Space>
          <PermWrapper resource='pulsar:sys:user:update'>
            <Link
              theme='primary'
              onClick={() => {
                setDetail(row);
                setVisible(true);
              }}
              hover='color'
            >
              编辑
            </Link>
          </PermWrapper>
          <PermWrapper resource='pulsar:sys:user:status'>
            <Link
              theme='primary'
              onClick={() => {
                setDetail(row);
                setStatusVisible(true);
              }}
              hover='color'
            >
              状态
            </Link>
          </PermWrapper>
          <PermWrapper resource='pulsar:sys:user:delete'>
            <Popconfirm theme='danger' content='确定删除？' onConfirm={() => doDelete(row)}>
              <Link theme='danger' hover='color'>
                删除
              </Link>
            </Popconfirm>
          </PermWrapper>
        </Space>
      ),
      width: 180,
    },
  ];

  const loadData = useMemoizedFn(async (params) => {
    const { data } = await getUserList({ ...params, page: 1, limit: 99999 });
    setData(data);
  });

  const { header, config, setData, doSearch, doRefresh } = useTable({
    columns,
    onSearch: loadData,
    search: (
      <>
        <FormItem name='username'>
          <Input clearable placeholder='请输入用户名称' />
        </FormItem>
        <FormItem name='roleId'>
          <Select filterable clearable options={options} placeholder='请输入角色' />
        </FormItem>
      </>
    ),
    operators: (
      <PermWrapper resource='pulsar:sys:user:create'>
        <Button onClick={() => setVisible(true)}>新增用户</Button>
      </PermWrapper>
    ),
  });

  const doDelete = async (row) => {
    await deleteUser({ id: row.id });
    MessagePlugin.success('操作成功');
    doRefresh();
  };

  useEffect(() => {
    doSearch();
    fetchRoleList({ page: 1, limit: 99999 });
  }, [doSearch, fetchRoleList]);

  const handleClose = (refresh) => {
    setVisible(false);
    setDetail(null);
    setStatusVisible(false);
    if (refresh) {
      doRefresh();
    }
  };

  return (
    <ListPanel header='用户管理'>
      {header}
      <Table {...config} />
      <FormModal visible={visible} detail={detail} options={options} handleClose={handleClose} />
      <StatusModal visible={statusVisible} detail={detail} handleClose={handleClose} />
    </ListPanel>
  );
}

export default connect(
  (state) => ({
    roles: state.role.roles,
  }),
  {
    fetchRoleList,
  },
)(User);
